import React from 'react';
import {
  Box, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TableSortLabel, Tooltip, Typography
} from '@mui/material';
import { ChevronRight as OpenIcon } from '@mui/icons-material';
import { fmtInt } from '../../OnlineJournal/format';
import { ListResponse, ProductRow, SortKey } from '../nomenclatureApi';
import { INVALID_LABELS, Sort } from '../nomenclatureCalc';
import { BORDER, headCellSx, noteSx, panelSx, rowSx, tableContainerSx } from '../nomenclatureStyles';
import { AuthImage, StatusChip, Value, imageOf } from './NomenclatureCommon';

interface Props {
  data: ListResponse | null;
  page: number;
  limit: number;
  sort: Sort;
  loading: boolean;
  hasFilters: boolean;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  onSort: (key: SortKey) => void;
  onOpen: (zid: string) => void;
  onResetFilters: () => void;
}

interface Column {
  id: string;
  label: string;
  width: number;
  sort?: SortKey;
  tip?: string;
}

const PAGE_SIZES = [25, 50, 100];
const ACTIONS_WIDTH = 44;

const COLUMNS: Column[] = [
  { id: 'image', label: '', width: 60 },
  { id: 'name', label: 'Товар', width: 320, sort: 'name', tip: 'Наименование 1С; ниже - название из Национального каталога, артикул и штрихкод' },
  { id: 'producer', label: 'Производитель', width: 190, sort: 'producer' },
  { id: 'mnn', label: 'МНН', width: 180, sort: 'mnn' },
  { id: 'type', label: 'Тип / папка', width: 170, sort: 'type' },
  { id: 'country', label: 'Страна', width: 120, sort: 'country', tip: 'Страна производства из НКТ' },
  { id: 'nkt', label: 'НКТ', width: 140, tip: 'Национальный каталог товаров: карточка по штрихкоду' },
  { id: 'ncels', label: 'НЦЭЛС', width: 160, tip: 'Госреестр лекарственных средств: регистрационное удостоверение' },
  { id: 'marked', label: 'Маркировка', width: 105, tip: 'Подлежит обязательной маркировке - по данным НКТ или ОФД' }
];
const MIN_WIDTH = COLUMNS.reduce((acc, c) => acc + c.width, ACTIONS_WIDTH);

const secondSx = { fontSize: 11.5, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

function NameCell({ r }: { r: ProductRow }) {
  const codes = [r.sku ? `арт. ${r.sku}` : '', r.barcode ? `ШК ${r.barcode}` : ''].filter(Boolean).join(' · ');
  const nktName = r.nktName && r.nktName.toLowerCase() !== r.name.toLowerCase() ? r.nktName : null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography title={r.name} sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {r.name}
      </Typography>
      {nktName && <Typography title={nktName} sx={secondSx}>{nktName}</Typography>}
      {codes && <Typography sx={{ ...noteSx, fontFamily: 'monospace' }}>{codes}</Typography>}
    </Box>
  );
}

function renderCell(id: string, r: ProductRow): React.ReactNode {
  switch (id) {
    case 'image': return <AuthImage image={imageOf(r.photo, r.boxId)} size="thumb" width={44} height={44} alt={r.name} />;
    case 'name': return <NameCell r={r} />;
    case 'producer': return <Value text={r.producer} title />;
    case 'mnn': return <Value text={r.mnn} title />;
    case 'type':
      return (
        <Box sx={{ minWidth: 0 }}>
          <Value text={[r.type, r.rxotc].filter(Boolean).join(' · ') || null} />
          {r.folder && <Typography title={r.folder} sx={secondSx}>{r.folder}</Typography>}
        </Box>
      );
    case 'country': return <Value text={r.country} title />;
    case 'nkt': return <StatusChip source="nkt" status={r.nkt} hint={r.invalidReason ? INVALID_LABELS[r.invalidReason] : undefined} />;
    case 'ncels':
      return (
        <Box sx={{ minWidth: 0 }}>
          <StatusChip source="ncels" status={r.ncels} />
          {r.regNumber && <Typography sx={{ ...noteSx, fontFamily: 'monospace', mt: 0.25 }}>{r.regNumber}</Typography>}
        </Box>
      );
    case 'marked':
      if (r.marked === null) return <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>-</Typography>;
      return <Typography sx={{ fontSize: 13, fontWeight: r.marked ? 600 : 400, color: r.marked ? '#047857' : '#64748b' }}>{r.marked ? 'Да' : 'Нет'}</Typography>;
    default: return null;
  }
}

/** Страница списка номенклатуры (листание и сортировка - на сервере); клик по строке - карточка товара */
const ProductsTable: React.FC<Props> = ({
  data, page, limit, sort, loading, hasFilters, onPage, onLimit, onSort, onOpen, onResetFilters
}) => {
  const rows = data ? data.items : [];
  const total = data ? data.total : 0;

  return (
    <Paper variant="outlined" sx={panelSx}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
          {data
            ? <>Товаров <Box component="b" sx={{ color: '#0f172a' }}>{fmtInt(total)}</Box>{hasFilters && ' (с учётом поиска и фильтров)'}</>
            : 'Загрузка списка…'}
        </Typography>
      </Box>

      <TableContainer sx={tableContainerSx}>
        <Table size="small" stickyHeader sx={{ minWidth: MIN_WIDTH, tableLayout: 'fixed', opacity: loading && rows.length ? 0.6 : 1, transition: 'opacity .15s' }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => {
                const label = col.sort ? (
                  <TableSortLabel active={sort.key === col.sort} direction={sort.key === col.sort ? sort.dir : 'asc'} onClick={() => onSort(col.sort as SortKey)}>
                    {col.label}
                  </TableSortLabel>
                ) : col.label;
                return (
                  <TableCell key={col.id} sx={{ ...headCellSx, width: col.width }}>
                    {col.tip ? <Tooltip title={col.tip} arrow><span>{label}</span></Tooltip> : label}
                  </TableCell>
                );
              })}
              <TableCell sx={{ ...headCellSx, width: ACTIONS_WIDTH }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.zid} hover onClick={() => onOpen(r.zid)} sx={{ ...rowSx, cursor: 'pointer' }}>
                {COLUMNS.map(col => (
                  <TableCell key={col.id} sx={{ overflow: 'hidden', pr: col.id === 'image' ? 0 : undefined }}>{renderCell(col.id, r)}</TableCell>
                ))}
                <TableCell align="center" sx={{ px: 0.5 }}>
                  <Tooltip title="Карточка товара">
                    <IconButton
                      size="small" aria-label="Карточка товара" sx={{ color: '#94a3b8' }}
                      onClick={e => { e.stopPropagation(); onOpen(r.zid); }}
                    >
                      <OpenIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {data && !rows.length && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">
                    {hasFilters ? 'По заданному поиску и фильтрам товаров не найдено' : 'Номенклатура ещё не загружена из 1С'}
                  </Typography>
                  {hasFilters && (
                    <Typography
                      variant="body2" onClick={onResetFilters}
                      sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Сбросить фильтры
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > PAGE_SIZES[0] && (
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={limit} rowsPerPageOptions={PAGE_SIZES}
          onPageChange={(_, p) => onPage(p)}
          onRowsPerPageChange={e => onLimit(Number(e.target.value))}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} из ${count.toLocaleString('ru-RU')}`}
          sx={{ borderTop: `1px solid ${BORDER}` }}
        />
      )}
    </Paper>
  );
};

export default React.memo(ProductsTable);
